import React, { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  TablePagination,
  TextField,
  IconButton,
  Checkbox,
  FormControlLabel,
  Menu,
  MenuItem,
} from '@mui/material';
import { FilterAlt, Delete } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import "./orderList.css";

const OrderList = () => {
  const [tableData, setTableData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState([]);
  const [userTypeFilter, setUserTypeFilter] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [anchorEl, setAnchorEl] = useState(null);
  const isFilterMenuOpen = Boolean(anchorEl);

  const navigate = useNavigate();

  const statuses = [
    'Pickup Scheduled',
    'Pickup Confirmed',
    'Payment Pending',
    'Order Completed',
    'Order Cancelled',
  ];
  const categories = ['general scrap', 'cloth', 'vehicle scrap'];
  const userTypes = ['individual', 'business'];

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const token = JSON.parse(sessionStorage.getItem('accessToken'));
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/user/scrap/getallscrap`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const reversedData = response.data.data.reverse(); 
        setTableData(reversedData);
        setFilteredData(reversedData);
      } catch (err) {
        console.error(err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, []);

  const handleSearch = (event) => {
    const keyword = event.target.value.toLowerCase();
    setSearch(keyword);
    applyFilters(keyword, statusFilter, categoryFilter, userTypeFilter);
  };

  const handleStatusChange = (status) => {
    const updatedFilter = statusFilter.includes(status)
      ? statusFilter.filter((s) => s !== status)
      : [...statusFilter, status];
    setStatusFilter(updatedFilter);
    applyFilters(search, updatedFilter, categoryFilter, userTypeFilter);
  };

  const handleCategoryChange = (category) => {
    const updatedFilter = categoryFilter.includes(category)
      ? categoryFilter.filter((c) => c !== category)
      : [...categoryFilter, category];
    setCategoryFilter(updatedFilter);
    applyFilters(search, statusFilter, updatedFilter, userTypeFilter);
  };

  const handleUserTypeChange = (userType) => {
    const updatedFilter = userTypeFilter.includes(userType)
      ? userTypeFilter.filter((u) => u !== userType)
      : [...userTypeFilter, userType];
    setUserTypeFilter(updatedFilter);
    applyFilters(search, statusFilter, categoryFilter, updatedFilter);
  };

  const applyFilters = (keyword, statuses, categories, userTypes) => {
    const filtered = tableData.filter((order) => {
      const matchesSearch =
        order.user?.username?.toLowerCase().includes(keyword) ||
        order.address?.toLowerCase().includes(keyword) ||
        order.user?.phone?.toLowerCase().includes(keyword);

      const matchesStatus =
        statuses.length > 0 ? statuses.includes(order.status) : true;

      const matchesCategory =
        categories.length > 0 ? categories.includes(order.category) : true;

      const matchesUserType =
        userTypes.length > 0
          ? userTypes.includes(order.user?.usertype) : true;

      return matchesSearch && matchesStatus && matchesCategory && matchesUserType;
    });

    setFilteredData(filtered);
    setPage(0);
  };

  const handleRowClick = (orderId) => {
    navigate(`/admin/orderDetails/${orderId}`);
  };

  const handleDeleteOrder = async (orderId) => {
    const confirmDelete = window.confirm(
      'Are you sure you want to delete this order?'
    );
    if (!confirmDelete) return;

    try {
      const token = JSON.parse(sessionStorage.getItem('accessToken'));
      await axios.delete(`${process.env.REACT_APP_API_URL}/user/scrap/deletescrap/${orderId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setFilteredData((prev) => prev.filter((order) => order._id !== orderId));
      setTableData((prev) => prev.filter((order) => order._id !== orderId));
      alert('Order deleted successfully.');
    } catch (err) {
      console.error(err);
      alert('Failed to delete order.');
    }
  };

  const handleFilterClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleFilterClose = () => {
    setAnchorEl(null);
  };

  if (loading) return <Typography>Loading...</Typography>;
  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <div style={{ padding: '40px' }}>
      <Typography variant="h3" style={{
        position: 'relative',
        top: 10,
      }}
        gutterBottom>
        Order List
      </Typography>

      {/* <Button
        variant="outlined"
        color="secondary"
        onClick={() => navigate('/admin/dashboard')} 
        style={{
          position: 'absolute',
          top: 10,
          left: 50,
        }}
      >
        Back
      </Button> */}
      {/* <Button
        variant="contained"
        color="primary"
        onClick={() => navigate('/admin/createOrder')}
        style={{
          position: 'absolute',
          top: 40,
          right: 50,
        }}
      >
        Create Order
      </Button> */}

      <TextField
        label="Search Orders"
        variant="outlined"
        fullWidth
        margin="normal"
        value={search}
        onChange={handleSearch}
        placeholder="Search by username, phone, or address"
      />

      <div style={{ marginBottom: '20px', textAlign: 'right' }}>
        <IconButton onClick={handleFilterClick}>
          <FilterAlt />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={isFilterMenuOpen}
          onClose={handleFilterClose}
          PaperProps={{
            style: { maxHeight: 400, width: '250px', padding: '10px' },
          }}
        >
          <MenuItem disabled>
            <Typography variant="subtitle1">Status</Typography>
          </MenuItem>
          {statuses.map((status) => (
            <MenuItem key={status} disableGutters>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={statusFilter.includes(status)}
                    onChange={() => handleStatusChange(status)}
                  />
                }
                label={status}
              />
            </MenuItem>
          ))}

          <MenuItem disabled>
            <Typography variant="subtitle1">Category</Typography>
          </MenuItem>
          {categories.map((category) => (
            <MenuItem key={category} disableGutters>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={categoryFilter.includes(category)}
                    onChange={() => handleCategoryChange(category)}
                  />
                }
                label={category}
              />
            </MenuItem>
          ))}

          <MenuItem disabled>
            <Typography variant="subtitle1">User Type</Typography>
          </MenuItem>
          {userTypes.map((userType) => (
            <MenuItem key={userType} disableGutters>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={userTypeFilter.includes(userType)}
                    onChange={() => handleUserTypeChange(userType)}
                  />
                }
                label={userType}
              />
            </MenuItem>
          ))}
        </Menu>
      </div>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Phone Number</TableCell>
              <TableCell>User Type</TableCell>
              <TableCell>Pickup Address</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Scheduled Date</TableCell>
              <TableCell>Scheduled Time</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((order) => (
                <TableRow
                  key={order._id}
                  hover
                  onClick={() => handleRowClick(order._id)}
                  sx={{
                    backgroundColor:
                      order.user?.usertype === 'individual'
                        ? '#e0f7fa'
                        : order.user?.usertype === 'business'
                          ? '#c8e6c9'
                          : '',
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.03)',
                      cursor: 'pointer', 
                    },
                  }}
                >
                  <TableCell>{order.user?.username}</TableCell>
                  <TableCell>{order.user?.phone}</TableCell>
                  <TableCell>{order.user?.usertype}</TableCell>
                  <TableCell>{order.address}</TableCell>
                  <TableCell
                    sx={{
                      color:
                        order.status === 'Pickup Scheduled'
                          ? '#00796b'
                          : order.status === 'Pickup Confirmed'
                            ? '#388e3c'
                            : order.status === 'Payment Pending'
                              ? '#ff9800'
                              : order.status === 'Order Cancelled'
                                ? '#d32f2f'
                                : order.status === 'Order Completed'
                                  ? '#512da8'
                                  : '',
                      fontWeight: 'bold',
                    }}
                  >
                    {order.status}
                  </TableCell>
                  <TableCell>{order.pickupdate}</TableCell>
                  <TableCell>{order.pickuptime}</TableCell>
                  <TableCell>{order.category}</TableCell>
                  <TableCell>
                    <IconButton
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteOrder(order._id);
                      }}
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={filteredData.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(event, newPage) => setPage(newPage)}
        onRowsPerPageChange={(event) =>
          setRowsPerPage(parseInt(event.target.value, 10))
        }
      />
    </div>
  );
};

export default OrderList;
